import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  ArrowRight, Sparkle, Books, PencilLine, ChartLineUp, Scissors, ShieldCheck,
  Lightning, Check, Minus, CaretDown, CircleNotch, PaperPlaneRight,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function TrialForm({ testidPrefix = "hero", large = false }) {
  const { trialSignup, error: authError } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    const res = await trialSignup(email.trim());
    setBusy(false);
    if (res) {
      toast.success(res.returning ? "Welcome back." : "Trial started. 5 drafts on us.");
      nav("/app/draft");
    } else {
      toast.error(authError || "Couldn't start trial. Try a different email.");
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-xl" data-testid={`${testidPrefix}-trial-form`}>
      <input
        type="email"
        required
        placeholder="you@company.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className={`input flex-1 ${large ? "h-[54px] text-base" : "h-[48px]"}`}
        data-testid={`${testidPrefix}-trial-email`}
      />
      <button type="submit" disabled={busy} className={`btn btn-accent ${large ? "btn-lg" : ""} whitespace-nowrap`} data-testid={`${testidPrefix}-trial-submit`}>
        {busy ? <><CircleNotch size={14} className="animate-spin"/> Opening</> : <>Start free <ArrowRight size={16}/></>}
      </button>
    </form>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(255,255,255,0.75)" }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="landing-brand">
          <span className="inline-block w-7 h-7 rounded-full" style={{ background: "var(--grad-warm)" }} />
          <span className="text-lg font-semibold tracking-tight">BrandLoop</span>
        </Link>
        <nav className="hidden md:flex items-center gap-1 text-sm p-1 rounded-full" style={{ color: "var(--ink-2)", background: "var(--paper)" }}>
          <a href="#how" className="px-4 py-1.5 rounded-full hover:bg-white transition-colors" data-testid="landing-nav-how">How it works</a>
          <a href="#preview" className="px-4 py-1.5 rounded-full hover:bg-white transition-colors" data-testid="landing-nav-preview">See it</a>
          <a href="#compare" className="px-4 py-1.5 rounded-full hover:bg-white transition-colors" data-testid="landing-nav-compare">Compare</a>
          <a href="#faq" className="px-4 py-1.5 rounded-full hover:bg-white transition-colors" data-testid="landing-nav-faq">FAQ</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm hidden sm:inline link-underline" data-testid="landing-signin" style={{ color: "var(--ink-2)" }}>Sign in</Link>
          <a href="#hero-form" className="btn btn-accent btn-sm" data-testid="landing-cta-nav">Start free <ArrowRight size={13}/></a>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden" style={{ background: "#fff" }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-14 relative">
        {/* Centered content */}
        <div className="flex flex-col items-center text-center relative">
          <div className="mb-6 fade-up">
            <span className="chip chip-accent" data-testid="hero-badge"><Sparkle size={12} weight="fill"/> Your personal brand manager · Start free</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-[72px] leading-[1.02] tracking-tight max-w-4xl font-semibold fade-up delay-1" style={{ textWrap: "balance" }} data-testid="landing-headline">
            Your personal brand just made its <span className="editorial grad-text whitespace-nowrap">first hire.</span>
          </h1>
          <p className="mt-7 text-lg lg:text-xl max-w-2xl fade-up delay-2" style={{ color: "var(--ink-2)" }}>
            Not another AI writer. A brand manager who learns your voice, your stories, your goals. The whole you. It creates LinkedIn content that grows your influence. And gets better at being yours every week.
          </p>

          <div id="hero-form" className="mt-9 w-full max-w-xl fade-up delay-3">
            <TrialForm testidPrefix="hero" large />
            <div className="mt-3 text-xs flex flex-wrap items-center justify-center gap-x-5 gap-y-1" style={{ color: "var(--ink-3)" }}>
              <span className="flex items-center gap-1.5"><Check size={12} weight="bold"/> No credit card</span>
              <span className="flex items-center gap-1.5"><Check size={12} weight="bold"/> Studio opens in seconds</span>
              <span className="flex items-center gap-1.5"><Check size={12} weight="bold"/> Cancel anytime</span>
            </div>
          </div>
        </div>

        {/* Product mock */}
        <div className="mt-16 lg:mt-20 relative fade-up delay-4">
          <div className="relative rounded-[32px] overflow-hidden float-long" style={{ background: "var(--bg-soft)", border: "1px solid var(--line)", boxShadow: "var(--sh-lift)" }}>
            <img
              src="/screenshots/drafting.jpeg?v=3"
              alt="BrandLoop Drafting Studio: chat, editable draft, and LinkedIn preview"
              className="w-full block"
              data-testid="hero-shot"
            />
          </div>
          {/* Floating accent chips around the mock */}
          <div className="hidden md:flex absolute -left-4 -bottom-4 items-center gap-2 float" data-testid="hero-chip-score">
            <div className="px-4 py-3 rounded-full flex items-center gap-2 glow-ring" style={{ background: "#fff", boxShadow: "var(--sh-card)", border: "1px solid var(--line)" }}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: "var(--accent)" }} />
              <span className="text-sm font-medium">Growth score · 8.3<span style={{ color: "var(--ink-3)" }}>/10</span></span>
            </div>
          </div>
          <div className="hidden md:flex absolute -right-2 top-8 items-center gap-2 float-long delay-2" data-testid="hero-chip-voice">
            <div className="px-4 py-3 rounded-full flex items-center gap-2" style={{ background: "var(--ink)", color: "#fff" }}>
              <Sparkle size={14} weight="fill" style={{ color: "var(--accent)" }}/>
              <span className="text-sm">Trained on your voice</span>
            </div>
          </div>
          <div className="hidden lg:flex absolute -left-6 top-16 items-center gap-2 drift delay-3" aria-hidden="true">
            <div className="px-3 py-2 rounded-full flex items-center gap-2 text-xs" style={{ background: "var(--accent-tint)", color: "var(--accent-2)", border: "1px solid var(--accent)" }}>
              <span className="w-1.5 h-1.5 rounded-full pulse-soft" style={{ background: "var(--accent)" }} />
              Rewriting hook…
            </div>
          </div>
          <div className="hidden lg:flex absolute right-6 -bottom-3 items-center gap-2 sway" aria-hidden="true">
            <div className="px-3 py-2 rounded-full flex items-center gap-2 text-xs" style={{ background: "#fff", border: "1px solid var(--line)", boxShadow: "var(--sh-card)" }}>
              <span className="editorial" style={{ color: "var(--rose)" }}>+42%</span>
              <span style={{ color: "var(--ink-3)" }}>saves this week</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustBar() {
  const items = ["Deep voice training", "AuthoredUp-grade analytics", "Live growth score", "Official LinkedIn API"];
  return (
    <div style={{ background: "var(--bg-soft)" }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm" style={{ color: "var(--ink-2)" }}>
        {items.map((t, i) => (
          <div key={i} className="flex items-center gap-2">
            <Check size={14} weight="bold" style={{ color: "var(--accent)" }}/>
            <span>{t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", title: "Feed her", body: "Upload playbooks, past posts, podcasts. She learns your voice.", icon: Books },
    { n: "02", title: "She writes", body: "Chat-based drafting grounded in your identity, stories, and swipes.", icon: PencilLine },
    { n: "03", title: "Growth score", body: "Live 0-10 rating on 6 LinkedIn signals as you edit.", icon: Lightning },
    { n: "04", title: "Loop closes", body: "Post performance flows back so her next draft is sharper.", icon: ChartLineUp },
  ];
  return (
    <section id="how" className="section">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-14">
          <div className="eyebrow mb-4">How it works</div>
          <h2 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
            Feed the loop, <span className="editorial grad-text">watch it compound.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
          {steps.map((s) => (
            <div key={s.n} className="card card-lift p-6 fade-up" data-testid={`how-step-${s.n}`}>
              <div className="flex items-center justify-between mb-8">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: "var(--accent-soft)", color: "var(--accent-2)" }}>
                  <s.icon size={22} weight="duotone"/>
                </div>
                <span className="font-mono text-xs" style={{ color: "var(--ink-4)" }}>{s.n}</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
              <p className="text-sm" style={{ color: "var(--ink-2)" }}>{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProductPreview() {
  const shots = [
    { key: "drafting",  src: "/screenshots/drafting.jpeg?v=3",  label: "Drafting Studio",   blurb: "Chat left. Editable draft right. Growth score updates as you type." },
    { key: "planner",   src: "/screenshots/planner.jpeg?v=3",   label: "Content Planner",   blurb: "Grid, Kanban, Table. Color-coded by pillar, drag to schedule." },
    { key: "kb",        src: "/screenshots/kb.jpeg?v=3",        label: "Knowledge Base",    blurb: "Upload PDF, Word, images, or paste. Auto-summarized." },
    { key: "storybank", src: "/screenshots/storybank.jpeg?v=3", label: "Storybank",         blurb: "Real stories, arcs extracted from your own words." },
    { key: "analytics", src: "/screenshots/analytics.jpeg?v=3", label: "Analytics",         blurb: "Feeds back into next week's drafts. Closes the loop." },
  ];
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(() => setActive((a) => (a + 1) % shots.length), 4200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, paused]);

  const s = shots[active];

  return (
    <section id="preview" className="section" style={{ background: "var(--bg-soft)" }}>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-10">
          <div className="eyebrow mb-4">See it in action</div>
          <h2 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
            One studio. <span className="editorial grad-text">Six pieces.</span>
          </h2>
          <p className="mt-4 text-lg max-w-xl" style={{ color: "var(--ink-2)" }}>
            Every module of BrandLoop is a piece of the same assistant. Click a tab, or let it play.
          </p>
        </div>

        {/* Tabs pill */}
        <div className="flex flex-wrap gap-2 mb-6" onMouseEnter={()=>setPaused(true)} onMouseLeave={()=>setPaused(false)}>
          {shots.map((sh, i) => (
            <button
              key={sh.key}
              onClick={()=>{setActive(i); setPaused(true);}}
              data-testid={`preview-tab-${sh.key}`}
              className={`chip transition-all`}
              style={i===active ? { background: "var(--ink)", color: "#fff", borderColor: "var(--ink)", transform: "scale(1.02)" } : {}}
            >
              {sh.label}
            </button>
          ))}
        </div>

        {/* Frame with animated screenshot swap */}
        <div className="relative" onMouseEnter={()=>setPaused(true)} onMouseLeave={()=>setPaused(false)}>
          <div className="relative rounded-[32px] overflow-hidden card card-lift" style={{ padding: 12 }} data-testid="preview-frame">
            <div className="rounded-[22px] overflow-hidden relative" style={{ background: "#fff", aspectRatio: "16 / 10", border: "1px solid var(--line)" }}>
              {shots.map((sh, i) => (
                <img
                  key={sh.key}
                  src={sh.src}
                  alt={sh.label}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                  style={{
                    opacity: i === active ? 1 : 0,
                    transform: i === active ? "scale(1)" : "scale(1.02)",
                    transition: "opacity 600ms ease, transform 900ms ease",
                  }}
                />
              ))}
            </div>
            {/* Floating caption card */}
            <div className="absolute left-8 -bottom-4 flex items-center gap-3 px-5 py-3 rounded-full float" style={{ background: "#fff", boxShadow: "var(--sh-card)", border: "1px solid var(--line)" }}>
              <span className="w-2 h-2 rounded-full" style={{ background: "var(--accent)" }}/>
              <span className="text-sm font-semibold">{s.label}</span>
              <span className="text-xs hidden sm:inline" style={{ color: "var(--ink-3)" }}>· {s.blurb}</span>
            </div>
          </div>

          {/* Progress dots */}
          <div className="mt-10 flex items-center justify-center gap-2">
            {shots.map((sh, i) => (
              <button
                key={sh.key}
                onClick={()=>{setActive(i); setPaused(true);}}
                aria-label={`Show ${sh.label}`}
                className="transition-all"
                style={{
                  height: 6,
                  width: i === active ? 32 : 8,
                  borderRadius: 999,
                  background: i === active ? "var(--accent)" : "var(--line-2)",
                  border: "none",
                  cursor: "pointer",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Compare() {
  // Sources: connectsafely.ai Kleo/Taplio/Supergrow 2026 reviews, kleo.so, taplio.com, supergrow.ai
  const rows = [
    ["Starting price",              "$10/mo",                 "$99/mo",                          "$39/mo (no AI)",              "$19/mo"],
    ["Free trial",                  "5 drafts, no card",      "30-day money-back (annual)",      "7 days, no card",              "7 days, no card"],
    ["Personal voice training",     "Chips + PDF/DOCX import","Deep, learns from your posts",    "Templates + hooks",           "Content DNA voice profile"],
    ["Storybank + arcs",            "Yes",                    "No",                              "No",                          "No"],
    ["Swipe pattern deconstruction","Yes",                    "Chrome-ext save only",            "5M+ post library, save only", "No"],
    ["Pre-publish growth score",    "Live, 6 signals",        "No",                              "No",                          "No"],
    ["Planner (Grid/Kanban/Table)", "All three",              "Kanban + scheduling",             "Kanban + scheduling",         "Kanban + scheduling"],
    ["Analytics on entry plan",     "Yes",                    "Very limited",                    "Basic only (AI on $65+)",     "None (Pro tier only)"],
    ["Official LinkedIn API",       "Yes",                    "Yes (web app)",                   "No public API",               "Own tool, no public API"],
  ];
  const cell = (v, brand) => {
    if (v === "Yes" || v === "All three" || v === "Live, 6 signals") return <span className="inline-flex items-center gap-1"><Check size={14} weight="bold" style={{color: brand?"var(--accent)":"var(--ink-2)"}}/> {v}</span>;
    if (v === "No" || v === "None (Pro tier only)") return <span className="inline-flex items-center gap-1" style={{color:"var(--ink-4)"}}><Minus size={14}/> {v}</span>;
    return v;
  };
  return (
    <section id="compare" className="section">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-10">
          <div className="eyebrow mb-4">How we compare</div>
          <h2 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
            Studio-grade depth. <span className="editorial grad-text">A fraction of the price.</span>
          </h2>
        </div>

        <div className="card overflow-hidden" data-testid="compare-table" style={{ boxShadow: "var(--sh-card)" }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th className="text-left px-6 py-6 eyebrow" style={{ width: "26%" }}></th>
                  <th className="text-left px-6 py-6" style={{ background: "var(--accent-tint)", borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-block w-4 h-4 rounded-full" style={{ background: "var(--grad-warm)" }}/>
                      <span className="text-lg font-semibold">BrandLoop</span>
                    </div>
                    <div className="chip chip-accent" style={{fontSize:10}}>Our pick</div>
                  </th>
                  <th className="text-left px-6 py-6">
                    <div className="text-lg font-semibold" style={{ color: "var(--ink-2)" }}>Kleo</div>
                    <div className="text-xs mt-1" style={{ color: "var(--ink-3)" }}>Depth benchmark</div>
                  </th>
                  <th className="text-left px-6 py-6">
                    <div className="text-lg font-semibold" style={{ color: "var(--ink-2)" }}>Taplio</div>
                    <div className="text-xs mt-1" style={{ color: "var(--ink-3)" }}>Scale player</div>
                  </th>
                  <th className="text-left px-6 py-6">
                    <div className="text-lg font-semibold" style={{ color: "var(--ink-2)" }}>Supergrow</div>
                    <div className="text-xs mt-1" style={{ color: "var(--ink-3)" }}>Value pick</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, bl, kleo, taplio, sg], i) => (
                  <tr key={i} className="fade-up">
                    <td className="px-6 py-4" style={{ color: "var(--ink-2)" }}>{label}</td>
                    <td className="px-6 py-4 font-medium" style={{ background: "var(--accent-tint)" }}>{cell(bl, true)}</td>
                    <td className="px-6 py-4" style={{ color: "var(--ink-3)" }}>{cell(kleo)}</td>
                    <td className="px-6 py-4" style={{ color: "var(--ink-3)" }}>{cell(taplio)}</td>
                    <td className="px-6 py-4" style={{ color: "var(--ink-3)" }}>{cell(sg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs mt-5 font-mono" style={{ color: "var(--ink-3)" }}>
          Sources: connectsafely.ai reviews, kleo.so, taplio.com/pricing, supergrow.ai/pricing (public info Jan 2026). Spotted an inaccuracy? Tell us, we fix it.
        </p>
      </div>
    </section>
  );
}

function FAQItem({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" data-testid={testid}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left px-6 py-5 group" data-testid={`${testid}-toggle`}>
        <span className="text-base font-medium pr-6">{q}</span>
        <CaretDown size={18} className="shrink-0" style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 220ms ease", color: "var(--ink-3)" }}/>
      </button>
      {open && (
        <div className="px-6 pb-6 -mt-2 text-sm fade-up" style={{ color: "var(--ink-2)" }}>{a}</div>
      )}
    </div>
  );
}

function FAQ() {
  const items = [
    { q: "How is this different from Kleo or Taplio?", a: "Kleo has depth but no team features, and starts at $99/mo. Taplio is scale-focused but shallow on voice. BrandLoop combines Kleo-depth personalization (voice training from your past posts, arcs from your stories) with a live growth score, real analytics feedback loop, and a much lower entry price." },
    { q: "Does the AI actually sound like me?", a: "Yes, provided you feed her. Paste 10-20 of your best posts into About Me (or upload a PDF/Word file), set your positioning and pillars, and she will match your vocabulary, rhythm, and hooks. The more you teach her, the sharper she gets." },
    { q: "Will it copy other creators' posts?", a: "No. The Swipe file deconstructs viral posts into pattern signals (hook type, structure, format) but never uses their wording. Every draft is built around your voice, your stories, and your rules." },
    { q: "What is the growth score?", a: "A live 0-10 rating on the six signals LinkedIn's algorithm uses: first-200 hook, dwell time, save utility, comment trigger, topic authority, and format compliance. It updates as you edit and gives one specific fix per signal." },
    { q: "Can I publish directly to LinkedIn?", a: "Copy-to-LinkedIn is one click today. Direct posting via LinkedIn's official API is on the roadmap (partner approval takes weeks, we are working it)." },
    { q: "What about my team and reviewers?", a: "Multi-profile workspaces with roles and approval workflows are on the P2 roadmap. For v1 we are laser-focused on nailing single-creator quality." },
    { q: "Is my data private?", a: "Yes. Your KB, stories, and identity are scoped to your account only, never shared with other users or used to train external models beyond your own drafting sessions." },
  ];
  return (
    <section id="faq" className="section" style={{ background: "var(--bg-soft)" }}>
      <div className="max-w-[900px] mx-auto px-6 lg:px-10">
        <div className="mb-12">
          <div className="eyebrow mb-4">Questions?</div>
          <h2 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
            Answered <span className="editorial grad-text">right here.</span>
          </h2>
        </div>
        <div className="space-y-3">
          {items.map((it, i) => <FAQItem key={i} testid={`faq-${i}`} q={it.q} a={it.a}/>)}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="section">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
        <div className="relative rounded-[36px] overflow-hidden p-10 lg:p-16 text-center" style={{ background: "var(--grad-pink)", color: "#fff" }}>
          {/* Soft blobs to add depth */}
          <div className="blob-blush" style={{ width: 520, height: 520, top: -140, left: "50%", transform: "translateX(-50%)", opacity: 0.6 }}/>
          <div className="blob-pink"  style={{ width: 320, height: 320, top: 60, right: -80, opacity: 0.55 }}/>
          <div className="blob-warm"  style={{ width: 260, height: 260, bottom: -80, left: -60, opacity: 0.35 }}/>

          <div className="relative">
            <div className="chip inline-flex mb-6" style={{ background: "rgba(255,255,255,0.85)", color: "var(--rose)", border: "none" }} data-testid="final-badge">
              <Sparkle size={12} weight="fill"/> 5 drafts on the house
            </div>
            <h2 className="text-4xl lg:text-6xl font-semibold tracking-tight leading-[1.02] max-w-3xl mx-auto" style={{ color: "#fff" }}>
              Your next post <span className="editorial" style={{ color: "#FFE9F0" }}>is 5 minutes</span> away.
            </h2>
            <p className="mt-5 max-w-xl mx-auto text-base" style={{ color: "rgba(255,255,255,0.92)" }}>
              Just your email. No card. Studio opens in seconds.
            </p>
            <div className="mt-10 flex justify-center">
              <div className="w-full max-w-lg">
                <TrialForm testidPrefix="final" large />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer>
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-10 flex flex-wrap items-center justify-between gap-6 text-sm" style={{ color: "var(--ink-3)" }}>
        <div className="flex items-center gap-2">
          <span className="inline-block w-5 h-5 rounded-full" style={{ background: "var(--grad-warm)" }} />
          <span className="font-semibold" style={{ color: "var(--ink)" }}>BrandLoop</span>
          <span className="ml-3">© 2026</span>
        </div>
        <a
          href="https://maidensail.com/startup/brandloop"
          rel="dofollow"
          target="_blank"
          data-testid="maidensail-badge"
          className="inline-flex items-center"
          title="Listed on Maidensail"
        >
          <img
            src="https://maidensail.com/badge/brandloop.svg"
            alt="Listed on Maidensail"
            height="36"
            style={{ height: 36 }}
          />
        </a>
        <div className="flex gap-6">
          <a href="#how" className="link-underline">How</a>
          <a href="#compare" className="link-underline">Compare</a>
          <a href="#faq" className="link-underline">FAQ</a>
          <Link to="/login" className="link-underline">Sign in</Link>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header/>
      <Hero/>
      <TrustBar/>
      <HowItWorks/>
      <ProductPreview/>
      <Compare/>
      <FAQ/>
      <FinalCTA/>
      <Footer/>
    </div>
  );
}
